import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

import { jsPDF } from 'jspdf';
import apiService, { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCanWrite } from '../components/Can';
import { formatTakaNo, seriesLabel } from '../utils/taka';
import { evalBillingFormula } from '../utils/billingFormula';
import BillingFormulaEditor from '../components/BillingFormulaEditor';

// Searchable Client Dropdown. `allowCreate=false` makes it a strict pick-from-list
// (no inline "+ Create", no free-text commit) — used for locked buyer parties.
const ClientDropdown: React.FC<{
  value: string;
  onChange: (name: string, clientId?: number, address?: string) => void;
  clients: any[];
  onCreateClient?: (name: string) => void;
  allowCreate?: boolean;
}> = ({ value, onChange, clients, onCreateClient, allowCreate = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 200 });
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 250) });
    }
  }, [isOpen]);

  const term = isOpen ? search : value;
  const filtered = clients.filter(c => c.isActive !== false && c.name.toLowerCase().includes((term || '').toLowerCase()));
  const showCreate = allowCreate && !!onCreateClient && term && !clients.some(c => c.name.toLowerCase() === term.toLowerCase());

  return (
    <div className="relative" ref={ref}>
      <input ref={inputRef} type="text" value={isOpen ? search : value}
        onChange={e => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
        onFocus={() => { setIsOpen(true); setSearch(value); }}
        placeholder="Search party..."
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
          {filtered.length > 0 ? filtered.map((c: any) => (
            <button key={c.id} type="button" tabIndex={-1}
              onClick={() => {
                const addr = c.addresses?.find((a: any) => a.isDefault) || c.addresses?.[0];
                onChange(c.name, c.id, addr?.address || '');
                setSearch(''); setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${value === c.name ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}>
              <div className="font-medium">{c.name}</div>
              {c.addresses?.[0]?.address && <div className="text-xs text-gray-400 truncate">{c.addresses[0].address}</div>}
            </button>
          )) : <div className="px-3 py-2 text-xs text-gray-400">No matches</div>}
          {showCreate && (
            <button type="button" tabIndex={-1}
              onClick={() => { onCreateClient?.(term.trim()); onChange(term.trim()); setSearch(''); setIsOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-green-700 bg-green-50 hover:bg-green-100 border-t border-gray-100 font-medium">
              + Create "{term}"
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

interface ChallanItem {
  takaInventoryId?: number;
  takaNo: number;
  series?: string | null;
  qualityName: string;
  desiNo: string;
  colour: string;
  mcNo: string;
  piece: string;
  meter: string;
  rate: string;
  amount: number;
}

interface ChallanData {
  id?: number;
  challanNo: number;
  date: string;
  acHead: string;
  partyName: string;
  address: string;
  rate: string;
  copyOfChallan: number;
  orderNo: string;
  totalPiece: number;
  totalMeter: number;
  totalAmount: number;
  items?: ChallanItem[];
}

// Searchable Quality Dropdown for challan form
const ChallanQualityDropdown: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { quality: string; count: number }[];
}> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.quality === value);
  const filtered = options.filter(o => o.quality.toLowerCase().includes((isOpen ? search : '').toLowerCase()));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
          (!dropRef.current || !dropRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [isOpen]);

  const open = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 3, left: r.left, width: Math.max(r.width, 280) });
    }
    setSearch(value);
    setIsOpen(true);
  };

  return (
    <>
      <input
        ref={triggerRef}
        type="text"
        value={isOpen ? search : (selected ? selected.quality : value)}
        onChange={e => { setSearch(e.target.value); if (!isOpen) open(); }}
        onFocus={open}
        placeholder="Search quality..."
        className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
      />
      {isOpen && createPortal(
        <div ref={dropRef}
          className="fixed z-[9999] bg-white border border-[#E1E6EF] rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] max-h-56 overflow-hidden"
          style={{ top: pos.top, left: pos.left, width: pos.width }}>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length > 0 ? filtered.map(o => (
              <button key={o.quality} type="button" tabIndex={-1}
                onClick={() => { onChange(o.quality); setSearch(''); setIsOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#F8F9FC] transition-colors ${value === o.quality ? 'bg-[#F8F9FC] text-[#0055a9] font-medium' : 'text-[#0E131B]'}`}>
                <span>{o.quality}</span>
                <span className="text-xs text-[rgba(14,19,27,0.4)]">{o.count} pending</span>
              </button>
            )) : <div className="px-3 py-3 text-sm text-[rgba(14,19,27,0.4)] text-center">No qualities found</div>}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const Challan: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'ADMIN';
  const canWrite = useCanWrite('stock');
  const [mode, setMode] = useState<'list' | 'add' | 'edit'>('list');
  const [editingChallanId, setEditingChallanId] = useState<number | null>(null);
  const [editingChallanNo, setEditingChallanNo] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Month-range summary PDF export (YYYY-MM)
  const [showExportRange, setShowExportRange] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportingRange, setExportingRange] = useState<'' | 'pdf' | 'xlsx'>('');
  const [printChallan, setPrintChallan] = useState<any>(null);
  const [printRows, setPrintRows] = useState(() => parseInt(localStorage.getItem('challan_printRows') || '14'));
  const [printCols, setPrintCols] = useState(() => parseInt(localStorage.getItem('challan_printCols') || '4'));
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const printSettingsRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Add form state
  const [formData, setFormData] = useState({
    acHead: 'job', partyName: '', address: '', date: new Date().toISOString().split('T')[0],
    rate: '', copyOfChallan: 1, orderNo: '', hsnCode: '', remark: ''
  });
  const [fromTaka, setFromTaka] = useState('');
  const [toTaka, setToTaka] = useState('');
  const [singleTaka, setSingleTaka] = useState('');
  // undefined = All Series (no filter), null = Default series, string = named series
  const [challanSeries, setChallanSeries] = useState<string | null | undefined>(undefined);
  const [selectedQuality, setSelectedQuality] = useState('');
  const [items, setItems] = useState<ChallanItem[]>([]);
  const [mixQualityConfirm, setMixQualityConfirm] = useState<{ takas: any[]; quality: string } | null>(null);
  const [viewChallan, setViewChallan] = useState<any>(null);
  const [deletingChallanId, setDeletingChallanId] = useState<number | null>(null);

  // Keyboard shortcuts for add mode
  useEffect(() => {
    if (mode !== 'add') return;
    const handler = (e: KeyboardEvent) => {
      // Ctrl+S / Cmd+S = Save & Print
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (items.length > 0) handleSave(true);
      }
      // Escape = Cancel / go back to list
      if (e.key === 'Escape' && !mixQualityConfirm) {
        e.preventDefault();
        setMode('list');
      }
      // Ctrl+L / Cmd+L = Load All
      if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
        e.preventDefault();
        loadAllTakas();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mode, items.length, mixQualityConfirm, selectedQuality]);

  // Inline edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [actionMenuId, setActionMenuId] = useState<number | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  // Close action menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setActionMenuId(null);
      }
    };
    if (actionMenuId !== null) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [actionMenuId]);

  // Close print settings on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (printSettingsRef.current && !printSettingsRef.current.contains(e.target as Node)) {
        setShowPrintSettings(false);
      }
    };
    if (showPrintSettings) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [showPrintSettings]);

  // Persist print settings to localStorage
  const updatePrintRows = (val: number) => {
    setPrintRows(val);
    localStorage.setItem('challan_printRows', String(val));
  };
  const updatePrintCols = (val: number) => {
    setPrintCols(val);
    localStorage.setItem('challan_printCols', String(val));
  };

  const { data: challanData, isLoading } = useQuery<{ challans: ChallanData[]; pagination: any }>({
    queryKey: ['challans', searchQuery, filterMonth, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (filterMonth) {
        const [y, m] = filterMonth.split('-').map(Number);
        params.set('startDate', `${y}-${String(m).padStart(2, '0')}-01`);
        const lastDay = new Date(y, m, 0).getDate();
        params.set('endDate', `${y}-${String(m).padStart(2, '0')}-${lastDay}`);
      }
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      return apiService.get(`/challans?${params}`);
    }
  });

  const challanList = challanData?.challans || [];
  const pagination = challanData?.pagination || { total: 0, page: 1, pageSize, totalPages: 1 };
  const totalRecords = pagination.total;
  const totalPages = pagination.totalPages;

  const { data: nextChallanNo } = useQuery({
    queryKey: ['nextChallanNo'],
    queryFn: async () => {
      const data = await apiService.get('/challans/next-challan-no');
      return data.nextChallanNo || 1;
    }
  });

  const { data: challanStats } = useQuery<any>({
    queryKey: ['challanStats', filterMonth],
    queryFn: async () => {
      const qs = filterMonth ? `?month=${encodeURIComponent(filterMonth)}` : '';
      return apiService.get(`/challans/stats${qs}`);
    }
  });

  // Approved buyer parties for the locked party-name dropdown (server falls back to all
  // clients for a tenant that hasn't curated a buyer list yet).
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ['clients', 'buyers'],
    queryFn: async () => {
      const data = await apiService.get('/clients?buyer=true');
      return data.clients || [];
    }
  });

  // Get distinct qualities from taka inventory for filter
  const { data: takaQualities = [] } = useQuery<string[]>({
    queryKey: ['takaQualities'],
    queryFn: async () => {
      const data = await apiService.get('/taka-inventory/distinct-qualities');
      return data.qualities || [];
    }
  });

  // Series list — shared cache key with TakaInventory.tsx
  const { data: challanSeriesList = [] } = useQuery<{ series: string | null; nextTakaNo: number; count: number }[]>({
    queryKey: ['takaSeriesList'],
    queryFn: async () => {
      const data = await apiService.get('/taka-inventory/series');
      return data.series || [];
    }
  });

  // Pending taka summary
  const { data: pendingSummary } = useQuery<{ summary: any[]; totalPending: number }>({
    queryKey: ['pendingTakaSummary'],
    queryFn: async () => apiService.get('/challans/pending-summary')
  });

  // Company settings for print
  const { data: companySettings } = useQuery({
    queryKey: ['adminSettings'],
    queryFn: async () => {
      const data = await apiService.get('/admin-settings');
      return data.settings;
    }
  });
  // Company-wide billing formula (null → app default inside evalBillingFormula)
  const billingFormula: string | undefined = (companySettings as any)?.billingFormula;

  const handlePrint = (challan: any) => {
    setPrintChallan(challan);
    // Print directly using persisted settings — no modal needed
    setTimeout(() => window.print(), 100);
  };

  const doPrint = () => {
    window.print();
  };

  const downloadPdf = (challan: any) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const margin = 8;
    const innerW = pw - margin * 2;
    const companyName = ((user as any)?.companyName || 'COMPANY NAME').toUpperCase();
    const allItems = challan.items || [];
    const totalMeterVal = parseFloat(challan.totalMeter || 0);
    const pdfClient = clients.find((c: any) => c.name === challan.partyName);

    const renderCopy = (startY: number) => {
      let y = startY;
      const x = margin;
      const boxTop = y;

      // --- Header ---
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      y += 4;
      doc.text('!! ** Shree Ganeshaya Namah ** !!', pw / 2, y, { align: 'center' });

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      y += 6;
      doc.text(companyName, pw / 2, y, { align: 'center' });

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      if (companySettings?.companyAddress) {
        y += 4;
        doc.text(companySettings.companyAddress.toUpperCase(), pw / 2, y, { align: 'center' });
      }
      if (companySettings?.companyPhone) {
        y += 3.5;
        doc.text(`Mobile : ${companySettings.companyPhone}`, pw / 2, y, { align: 'center' });
      }
      if (companySettings?.companyGst) {
        y += 3.5;
        doc.text(`GSTIN : ${companySettings.companyGst}`, pw / 2, y, { align: 'center' });
      }

      // --- DELIVERY CHALLAN banner ---
      y += 3;
      doc.setLineWidth(0.4);
      doc.line(x, y, x + innerW, y);
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('DELIVERY CHALLAN', pw / 2, y, { align: 'center' });
      y += 2;
      doc.line(x, y, x + innerW, y);

      // --- Party & Challan Info ---
      y += 4;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Party :', x + 2, y);
      doc.setFont('helvetica', 'normal');
      doc.text(` ${challan.partyName || ''}`, x + 2 + doc.getTextWidth('Party :'), y);

      if (challan.address) {
        y += 3.5;
        doc.setFontSize(8);
        doc.text(challan.address, x + 16, y);
      }
      if (pdfClient?.gstNo) {
        y += 3.5;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('GSTIN :', x + 2, y);
        doc.setFont('helvetica', 'normal');
        doc.text(` ${pdfClient.gstNo}`, x + 2 + doc.getTextWidth('GSTIN :'), y);
      }

      // Challan No / Date box (top right)
      const boxW = 52;
      const boxX = x + innerW - boxW;
      const infoY = y - (challan.address ? 7 : 3.5);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      // Chln No row
      doc.rect(boxX, infoY - 3.5, boxW / 2, 6);
      doc.rect(boxX + boxW / 2, infoY - 3.5, boxW / 2, 6);
      doc.text('Chln No', boxX + boxW / 4, infoY, { align: 'center' });
      doc.text(String(challan.challanNo), boxX + boxW * 3 / 4, infoY, { align: 'center' });
      // Date row
      doc.rect(boxX, infoY + 2.5, boxW / 2, 6);
      doc.rect(boxX + boxW / 2, infoY + 2.5, boxW / 2, 6);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', boxX + boxW / 4, infoY + 6, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(new Date(challan.date).toLocaleDateString('en-IN'), boxX + boxW * 3 / 4, infoY + 6, { align: 'center' });

      // --- Quality / Broker / HSN row ---
      // Ensure y is below the date box
      const dateBoxBottom = infoY + 8.5;
      y = Math.max(y + 3, dateBoxBottom + 1);
      doc.setLineWidth(0.2);
      doc.line(x, y, x + innerW, y);
      y += 3.5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      const qualityVal = challan.items?.[0]?.qualityName || challan.items?.[0]?.quality_name || '';
      doc.text('Quality :' + qualityVal, x + 2, y);
      doc.text('Broker Name :', pw / 2 - 15, y);
      doc.text('HSN Code :' + (challan.hsnCode || ''), pw / 2 + 25, y);
      y += 2;
      doc.line(x, y, x + innerW, y);

      // --- Items Table ---
      y += 1;
      const rowsPerCol = printRows;
      const maxCols = printCols;
      const itemsPerPage = rowsPerCol * maxCols;
      const pageItems = allItems.slice(0, itemsPerPage);

      const colGap = maxCols > 1 ? 2 : 0;
      const colW = (innerW - colGap * (maxCols - 1)) / maxCols;
      const srlW = colW * 0.12;
      const takaW = colW * 0.28;
      const mtrW = colW * 0.35;
      const mcW = colW * 0.25;
      const rowH = 4.2;

      const tableStartY = y;

      for (let colIdx = 0; colIdx < maxCols; colIdx++) {
        const colItems = pageItems.slice(colIdx * rowsPerCol, (colIdx + 1) * rowsPerCol);
        const colX = x + colIdx * (colW + colGap);
        let ty = tableStartY;

        // Header
        doc.setFillColor(240, 240, 240);
        doc.rect(colX, ty, colW, rowH, 'FD');
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.text('Srl', colX + srlW / 2, ty + 3, { align: 'center' });
        doc.text('TakaNo', colX + srlW + takaW / 2, ty + 3, { align: 'center' });
        doc.text('Meters', colX + srlW + takaW + mtrW / 2, ty + 3, { align: 'center' });
        doc.text('MC No.', colX + srlW + takaW + mtrW + mcW / 2, ty + 3, { align: 'center' });
        // Header cell borders
        doc.rect(colX, ty, srlW, rowH);
        doc.rect(colX + srlW, ty, takaW, rowH);
        doc.rect(colX + srlW + takaW, ty, mtrW, rowH);
        doc.rect(colX + srlW + takaW + mtrW, ty, mcW, rowH);
        ty += rowH;

        // Data rows
        const globalStart = colIdx * rowsPerCol;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        for (let i = 0; i < rowsPerCol; i++) {
          const item = colItems[i];
          // Cell borders
          doc.rect(colX, ty, srlW, rowH);
          doc.rect(colX + srlW, ty, takaW, rowH);
          doc.rect(colX + srlW + takaW, ty, mtrW, rowH);
          doc.rect(colX + srlW + takaW + mtrW, ty, mcW, rowH);

          if (item) {
            doc.setTextColor(220, 0, 0);
            doc.text(String(globalStart + i + 1), colX + srlW / 2, ty + 3, { align: 'center' });
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'bold');
            doc.text(formatTakaNo(item.series, item.takaNo || item.taka_no), colX + srlW + takaW / 2, ty + 3, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.text(parseFloat(item.meter || 0).toFixed(2), colX + srlW + takaW + mtrW - 2, ty + 3, { align: 'right' });
            const mcNo = item.mcNo || item.mc_no || item.takaInventory?.mcNo || item.takaInventory?.mc_no || '';
            doc.text(String(mcNo), colX + srlW + takaW + mtrW + mcW / 2, ty + 3, { align: 'center' });
          }
          ty += rowH;
        }

        // Column subtotal row
        const colMeterTotal = colItems.reduce((s: number, it: any) => s + parseFloat(it.meter || 0), 0);
        doc.rect(colX, ty, srlW + takaW, rowH);
        doc.rect(colX + srlW + takaW, ty, mtrW, rowH);
        doc.rect(colX + srlW + takaW + mtrW, ty, mcW, rowH);
        if (colItems.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text(colMeterTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), colX + srlW + takaW + mtrW - 2, ty + 3, { align: 'right' });
        }
        doc.setFont('helvetica', 'normal');
      }

      y = tableStartY + (rowsPerCol + 2) * rowH;

      // --- Totals ---
      doc.setLineWidth(0.4);
      doc.line(x, y, x + innerW, y);
      y += 4;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Taka : ${allItems.length}`, x + 2, y);
      doc.text(`Total Mtrs : ${totalMeterVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, x + 50, y);

      // --- Remark ---
      if (challan.remark) {
        y += 2;
        doc.setLineWidth(0.2);
        doc.line(x, y, x + innerW, y);
        y += 3.5;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`REMARKS:- ${challan.remark}`, x + 2, y);
      }

      // --- Footer ---
      y += 2;
      doc.setLineWidth(0.2);
      doc.line(x, y, x + innerW, y);
      y += 4;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const prepName = (user as any)?.name || '';
      // Receiver Sign with drawn line
      doc.text('Receiver Sign', x + 2, y);
      const recLineX = x + 2 + doc.getTextWidth('Receiver Sign ');
      doc.line(recLineX, y + 0.5, recLineX + 25, y + 0.5);
      y += 4;
      // Prepared By with drawn line and name
      doc.text('Prepared By', x + 2, y);
      const prepLineX = x + 2 + doc.getTextWidth('Prepared By ');
      doc.line(prepLineX, y + 0.5, prepLineX + 25, y + 0.5);
      doc.text(prepName, prepLineX + 27, y);

      // Right side footer
      doc.setFont('helvetica', 'normal');
      doc.text('For,', x + innerW - 30, y - 4);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName, x + innerW - 30, y);
      doc.setFont('helvetica', 'normal');
      doc.text('Auth.Signatory', x + innerW - 30, y + 6);

      y += 8;

      // Outer border
      doc.setLineWidth(0.4);
      doc.rect(x, boxTop, innerW, y - boxTop);

      return y;
    };

    // Copy 1 — top half
    const copy1End = renderCopy(6);

    // Dashed separator
    const sepY = copy1End + 3;
    doc.setLineDashPattern([2, 2], 0);
    doc.setLineWidth(0.2);
    doc.line(margin, sepY, margin + innerW, sepY);
    doc.setLineDashPattern([], 0);

    // Copy 2 — bottom half
    renderCopy(sepY + 3);

    // Software credit
    const creditY = doc.internal.pageSize.getHeight() - 4;
    doc.setFontSize(6);
    doc.setTextColor(140, 140, 140);
    doc.setFont('helvetica', 'normal');
    const creditText = 'Software by ';
    const brandText = 'VueNexa Enterprises';
    const creditWidth = doc.getTextWidth(creditText);
    doc.setFont('helvetica', 'bold');
    const brandWidth = doc.getTextWidth(brandText);
    doc.setFont('helvetica', 'normal');
    const totalCreditW = creditWidth + brandWidth;
    const creditStartX = (pw - totalCreditW) / 2;
    doc.text(creditText, creditStartX, creditY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 46, 150);
    doc.text(brandText, creditStartX + creditWidth, creditY);
    doc.setTextColor(0, 0, 0);

    doc.save(`Challan_${challan.challanNo}_${challan.partyName || 'unknown'}.pdf`);
  };

  // Summary PDF for a month range: one row per challan, a subtotal after each month,
  // grand total at the end. Fetches every page of /challans for the range.
  const exportRangePdf = async () => {
    if (!exportFrom || !exportTo || exportFrom > exportTo) return;
    setExportingRange('pdf');
    try {
      const [fy, fm] = exportFrom.split('-').map(Number);
      const [ty, tm] = exportTo.split('-').map(Number);
      const startDate = `${fy}-${String(fm).padStart(2, '0')}-01`;
      const endDate = `${ty}-${String(tm).padStart(2, '0')}-${new Date(ty, tm, 0).getDate()}`;
      const rows: any[] = [];
      for (let p = 1; ; p++) {
        const res: any = await apiService.get(`/challans?startDate=${startDate}&endDate=${endDate}&page=${p}&pageSize=500`);
        rows.push(...(res.challans || []));
        if (p >= (res.pagination?.totalPages || 1)) break;
      }
      if (!rows.length) { toast.error('No challans in this range'); return; }
      // Challan numbers can restart each financial year, so order by date first.
      rows.sort((a, b) => String(a.date).localeCompare(String(b.date)) || a.challanNo - b.challanNo);

      const toLocalDate = (s: string) => { const [y, m, d] = String(s).slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); };
      const monthLabel = (ym: string) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }); };
      const fmt = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const margin = 10;
      const bottom = ph - 12;
      const companyName = ((user as any)?.companyName || companySettings?.companyName || 'COMPANY NAME').toUpperCase();
      const rangeText = exportFrom === exportTo ? monthLabel(exportFrom) : `${monthLabel(exportFrom)} to ${monthLabel(exportTo)}`;

      // Column layout (widths sum to pw - 2 * margin = 277mm)
      const cols = [
        { key: 'no', label: '#CHALLAN', w: 18, align: 'left' },
        { key: 'party', label: 'PARTY NAME', w: 95, align: 'left' },
        { key: 'date', label: 'DATE', w: 26, align: 'left' },
        { key: 'type', label: 'TYPE', w: 16, align: 'left' },
        { key: 'items', label: 'ITEMS', w: 16, align: 'right' },
        { key: 'piece', label: 'PIECE', w: 18, align: 'right' },
        { key: 'meter', label: 'METER', w: 28, align: 'right' },
        { key: 'amount', label: 'AMOUNT (Rs)', w: 36, align: 'right' },
        { key: 'status', label: 'STATUS', w: 24, align: 'left' },
      ] as const;
      const colX: Record<string, number> = {};
      let cx = margin;
      cols.forEach(c => { colX[c.key] = cx; cx += c.w; });
      const cellText = (key: string, text: string, y: number) => {
        const c = cols.find(col => col.key === key)!;
        if (c.align === 'right') doc.text(text, colX[key] + c.w - 2, y, { align: 'right' });
        else doc.text(text, colX[key] + 2, y);
      };

      let y = margin;
      const drawTableHeader = () => {
        doc.setFillColor(248, 249, 252);
        doc.rect(margin, y, pw - margin * 2, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(90, 95, 105);
        cols.forEach(c => cellText(c.key, c.label, y + 4.7));
        doc.setTextColor(0, 0, 0);
        y += 7;
      };
      const ensureSpace = (h: number) => {
        if (y + h <= bottom) return;
        doc.addPage();
        y = margin;
        drawTableHeader();
      };
      const drawTotalRow = (label: string, t: { items: number; piece: number; meter: number; amount: number }, grand: boolean) => {
        ensureSpace(8);
        if (grand) doc.setFillColor(0, 85, 169); else doc.setFillColor(234, 240, 250);
        doc.rect(margin, y, pw - margin * 2, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        if (grand) doc.setTextColor(255, 255, 255); else doc.setTextColor(14, 19, 27);
        doc.text(label, margin + 2, y + 5.3);
        cellText('items', String(t.items), y + 5.3);
        cellText('piece', String(t.piece), y + 5.3);
        cellText('meter', t.meter.toFixed(2), y + 5.3);
        cellText('amount', fmt(t.amount), y + 5.3);
        doc.setTextColor(0, 0, 0);
        y += 8;
      };

      // Title block (first page only)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(companyName, pw / 2, y + 5, { align: 'center' });
      doc.setFontSize(11);
      doc.text(`Challan Summary: ${rangeText}`, pw / 2, y + 11, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(110, 115, 125);
      doc.text(`Generated ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, pw - margin, y + 11, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      y += 16;
      drawTableHeader();

      const zero = () => ({ count: 0, items: 0, piece: 0, meter: 0, amount: 0 });
      const grand = zero();
      let monthKey = '';
      let monthT = zero();
      const flushMonth = () => {
        if (!monthKey) return;
        drawTotalRow(`${monthLabel(monthKey)} total  (${monthT.count} chln)`, monthT, false);
      };

      rows.forEach(c => {
        const key = String(c.date).slice(0, 7);
        if (key !== monthKey) { flushMonth(); monthKey = key; monthT = zero(); }

        const qualities = Array.from(new Set((c.items || []).map((it: any) => it.qualityName || it.quality_name).filter(Boolean))) as string[];
        const rowH = qualities.length ? 10 : 7;
        ensureSpace(rowH);

        const itemsN = c.items?.length || 0;
        const piece = Number(c.totalPiece) || 0;
        const meter = parseFloat(c.totalMeter || 0) || 0;
        const amount = parseFloat(c.totalAmount || 0) || 0;
        const partyW = cols[1].w - 4;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        cellText('no', `#${c.challanNo}`, y + 4.7);
        doc.text(doc.splitTextToSize(c.partyName || '-', partyW)[0], colX.party + 2, y + 4.7);
        if (qualities.length) {
          doc.setFontSize(7);
          doc.setTextColor(120, 125, 135);
          doc.text(doc.splitTextToSize(qualities.join(', '), partyW)[0], colX.party + 2, y + 8.2);
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(9);
        }
        cellText('date', toLocalDate(c.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }), y + 4.7);
        cellText('type', c.acHead === 'job' ? 'Job' : 'Own', y + 4.7);
        cellText('items', String(itemsN), y + 4.7);
        cellText('piece', String(piece), y + 4.7);
        cellText('meter', meter.toFixed(2), y + 4.7);
        cellText('amount', fmt(amount), y + 4.7);
        doc.setFont('helvetica', 'bold');
        if (c.billId) doc.setTextColor(0, 105, 8); else doc.setTextColor(168, 44, 0);
        cellText('status', c.billId ? 'Billed' : 'Pending', y + 4.7);
        doc.setTextColor(0, 0, 0);

        doc.setDrawColor(235, 238, 244);
        doc.line(margin, y + rowH, pw - margin, y + rowH);
        y += rowH;

        for (const t of [monthT, grand]) { t.count++; t.items += itemsN; t.piece += piece; t.meter += meter; t.amount += amount; }
      });
      flushMonth();
      y += 2;
      drawTotalRow(`GRAND TOTAL  (${grand.count} chln)`, grand, true);

      // Page numbers
      const pages = doc.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(140, 145, 155);
        doc.text(`${companyName} - Challan Summary ${rangeText}`, margin, ph - 6);
        doc.text(`Page ${i} of ${pages}`, pw - margin, ph - 6, { align: 'right' });
      }

      doc.save(`Challans_${exportFrom}_to_${exportTo}.pdf`);
      setShowExportRange(false);
    } catch {
      toast.error('Export failed');
    } finally {
      setExportingRange('');
    }
  };

  // Same summary as an .xlsx, built server-side (ExcelJS).
  const exportRangeExcel = async () => {
    if (!exportFrom || !exportTo || exportFrom > exportTo) return;
    setExportingRange('xlsx');
    try {
      const res = await api.get(`/challans/export-excel?from=${exportFrom}&to=${exportTo}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Challans_${exportFrom}_to_${exportTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowExportRange(false);
    } catch (err: any) {
      toast.error(err?.response?.status === 404 ? 'No challans in this range' : 'Export failed');
    } finally {
      setExportingRange('');
    }
  };

  const shouldPrintAfterSave = useRef(false);

  const createMutation = useMutation({
    mutationFn: async (data: any) => apiService.post('/challans', data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['challans'] });
      queryClient.invalidateQueries({ queryKey: ['challanStats'] });
      queryClient.invalidateQueries({ queryKey: ['nextChallanNo'] });
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      if (shouldPrintAfterSave.current && response?.challan) {
        handlePrint(response.challan);
      }
      shouldPrintAfterSave.current = false;
      setMode('list');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to create challan')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => apiService.put(`/challans/${id}`, data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['challans'] });
      queryClient.invalidateQueries({ queryKey: ['challanStats'] });
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      if (shouldPrintAfterSave.current && response?.challan) {
        handlePrint(response.challan);
      }
      shouldPrintAfterSave.current = false;
      setEditingChallanId(null);
      setMode('list');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update challan')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiService.delete(`/challans/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challans'] });
      queryClient.invalidateQueries({ queryKey: ['challanStats'] });
      queryClient.invalidateQueries({ queryKey: ['nextChallanNo'] });
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to delete')
  });

  const openAdd = () => {
    setFormData({
      acHead: 'job', partyName: '', address: '', date: new Date().toISOString().split('T')[0],
      rate: '', copyOfChallan: 1, orderNo: '', hsnCode: '', remark: ''
    });
    setFromTaka(''); setToTaka(''); setSingleTaka(''); setItems([]);
    setMode('add');
  };

  const openEdit = (challan: any) => {
    setEditingChallanId(challan.id);
    setEditingChallanNo(challan.challanNo);
    setFormData({
      acHead: challan.acHead || 'job',
      partyName: challan.partyName || '',
      address: challan.address || '',
      date: challan.date ? challan.date.split('T')[0] : new Date().toISOString().split('T')[0],
      rate: String(challan.rate || ''),
      copyOfChallan: challan.copyOfChallan || 1,
      orderNo: challan.orderNo || '',
      hsnCode: challan.hsnCode || '',
      remark: challan.remark || ''
    });
    setItems((challan.items || []).map((item: any) => ({
      takaInventoryId: item.takaInventoryId,
      takaNo: item.takaNo || item.taka_no,
      series: (item.series ?? item.taka_series) ?? null,
      qualityName: item.qualityName || item.quality_name || '',
      desiNo: item.desiNo || item.desi_no || '',
      colour: item.colour || '',
      mcNo: item.mcNo || item.mc_no || item.takaInventory?.mcNo || '',
      avgPick: String(item.avgPick || item.avg_pick || item.takaInventory?.avgPick || item.takaInventory?.avg_pick || item.taka_inventory?.avgPick || item.taka_inventory?.avg_pick || ''),
      piece: String(item.piece || '1'),
      meter: String(item.meter || ''),
      rate: String(item.rate || challan.rate || ''),
      amount: parseFloat(item.amount || 0)
    })));
    setFromTaka(''); setToTaka(''); setSingleTaka('');
    setMode('edit');
  };

  // Amount per taka from the company's configured billing formula (falls back to the app default).
  const calcAmount = (meter: string, avgPick: string, rate: string, piece: string = '1') => {
    return evalBillingFormula(billingFormula, {
      rate: parseFloat(rate) || 0,
      meter: parseFloat(meter) || 0,
      avgPick: parseFloat(avgPick) || 0,
      piece: parseFloat(piece) || 0,
    });
  };

  const mapTaka = (t: any) => {
    const rate = formData.rate || '';
    return {
      takaInventoryId: t.id,
      takaNo: t.takaNo,
      series: t.series ?? null,
      qualityName: t.qualityDesiNo || '',
      desiNo: '',
      colour: t.colour || '',
      mcNo: t.mcNo || '',
      avgPick: t.avgPick || '',
      piece: '1',
      meter: String(t.meter || ''),
      rate,
      amount: calcAmount(String(t.meter || 0), String(t.avgPick || 0), rate)
    };
  };

  // Check if new takas have a different quality than existing items
  const checkAndAddTakas = (newTakas: any[]) => {
    if (!newTakas.length) return;
    const existingQualities = new Set(items.map(i => i.qualityName).filter(Boolean));
    const newQualities = new Set(newTakas.map((t: any) => t.qualityDesiNo || '').filter(Boolean));
    const hasMismatch = existingQualities.size > 0 && Array.from(newQualities).some(q => !existingQualities.has(q));

    if (hasMismatch) {
      setMixQualityConfirm({ takas: newTakas, quality: Array.from(newQualities).join(', ') });
    } else {
      // Prepend new takas so most recently added appear at the top
      const existingIds = new Set(items.map(i => i.takaInventoryId));
      const mapped = newTakas.filter((t: any) => !existingIds.has(t.id)).map(mapTaka);
      setItems(prev => [...mapped, ...prev]);
    }
  };

  const confirmMixQuality = () => {
    if (mixQualityConfirm) {
      const existingIds = new Set(items.map(i => i.takaInventoryId));
      const mapped = mixQualityConfirm.takas.filter((t: any) => !existingIds.has(t.id)).map(mapTaka);
      setItems(prev => [...mapped, ...prev]);
      setMixQualityConfirm(null);
    }
  };

  // When editing, exclude current challan's items from "already used" filter
  const editExclude = mode === 'edit' && editingChallanId ? `&excludeChallanId=${editingChallanId}` : '';

  // Build the series query fragment. undefined = All Series (omit param entirely).
  const buildSeriesFragment = (leading = '&') => {
    if (challanSeries === undefined) return '';
    const value = challanSeries === null ? '' : encodeURIComponent(challanSeries);
    return `${leading}series=${value}`;
  };

  const loadTakas = async () => {
    if (!fromTaka || !toTaka) { toast.error('Enter taka range'); return; }
    if (!selectedQuality) { toast.error('Select a quality first'); return; }
    try {
      const data = await apiService.get(`/challans/taka-range?fromTaka=${fromTaka}&toTaka=${toTaka}&quality=${encodeURIComponent(selectedQuality)}${buildSeriesFragment('&')}${editExclude}`);
      const takas = data.takas || [];
      if (!takas.length) { toast.error('No takas found in this range for selected quality'); return; }
      checkAndAddTakas(takas);
    } catch { toast.error('Failed to load takas'); }
  };

  const loadAllTakas = async () => {
    try {
      const qualityPart = selectedQuality ? `quality=${encodeURIComponent(selectedQuality)}` : '';
      const seriesPart = buildSeriesFragment(qualityPart ? '&' : '');
      const url = `/challans/available-takas?${qualityPart}${seriesPart}${editExclude}`;
      const data = await apiService.get(url);
      const takas = data.takas || [];
      if (!takas.length) { toast.error(selectedQuality ? `No available takas for "${selectedQuality}".` : 'No pending takas found.'); return; }
      checkAndAddTakas(takas);
    } catch { toast.error('Failed to load takas'); }
  };

  const loadSingleTaka = async () => {
    if (!singleTaka) { toast.error('Enter a taka number'); return; }
    try {
      const params = `fromTaka=${singleTaka}&toTaka=${singleTaka}${buildSeriesFragment('&')}${selectedQuality ? `&quality=${encodeURIComponent(selectedQuality)}` : ''}${editExclude}`;
      const data = await apiService.get(`/challans/taka-range?${params}`);
      const takas = data.takas || [];
      if (!takas.length) { toast.error(`Taka #${singleTaka} not found or already assigned`); return; }
      checkAndAddTakas(takas);
      setSingleTaka('');
    } catch { toast.error('Failed to load taka'); }
  };

  const updateItem = useCallback((index: number, field: keyof ChallanItem, value: string) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      const meter = parseFloat(next[index].meter) || 0;
      const avgPick = parseFloat((next[index] as any).avgPick) || 0;
      const rate = parseFloat(next[index].rate) || parseFloat(formData.rate) || 0;
      const piece = parseFloat(next[index].piece) || 1;
      next[index].amount = evalBillingFormula(billingFormula, { rate, meter, avgPick, piece });
      return next;
    });
  }, [formData.rate, billingFormula]);

  const removeItem = (index: number) => setItems(prev => prev.filter((_, i) => i !== index));

  const handleSave = (andPrint = false) => {
    if (!formData.partyName.trim()) { toast.error('Party name is required'); return; }
    if (!formData.rate || !parseFloat(formData.rate)) { toast.error('Rate is required'); return; }
    if (!items.length) { toast.error('Load takas first'); return; }
    shouldPrintAfterSave.current = andPrint;
    const itemsPayload = items.map(it => ({
      takaInventoryId: it.takaInventoryId,
      takaNo: it.takaNo,
      series: it.series ?? null,
      qualityName: it.qualityName || null,
      desiNo: it.desiNo || null,
      colour: it.colour || null,
      piece: parseFloat(it.piece) || 0,
      meter: parseFloat(it.meter) || 0,
      rate: parseFloat(it.rate) || parseFloat(formData.rate) || 0,
      avgPick: (it as any).avgPick ? parseFloat((it as any).avgPick) : undefined
    }));
    const commonPayload = {
      date: formData.date,
      acHead: formData.acHead,
      partyName: formData.partyName || null,
      address: formData.address || null,
      rate: parseFloat(formData.rate) || 0,
      copyOfChallan: formData.copyOfChallan,
      orderNo: formData.orderNo || null,
      hsnCode: formData.hsnCode || null,
      remark: formData.remark || null,
      items: itemsPayload
    };
    if (mode === 'edit' && editingChallanId) {
      // Never update challanNo on edit
      updateMutation.mutate({ id: editingChallanId, data: commonPayload });
    } else {
      createMutation.mutate({ ...commonPayload, challanNo: nextChallanNo || 1 });
    }
  };

  const totalPiece = items.reduce((s, i) => s + (parseFloat(i.piece) || 0), 0);
  const totalMeter = items.reduce((s, i) => s + (parseFloat(i.meter) || 0), 0);
  const totalAmount = items.reduce((s, i) => s + (i.amount || 0), 0);

  const formatINR = (val: number) => '₹' + val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const monthLabel = filterMonth ? new Date(filterMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : 'All';

  // ADD MODE
  if (mode === 'add' || mode === 'edit') {
    return (
      <motion.div
        key="add-mode"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -30 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className={items.length > 0 ? "h-[calc(100vh-120px)] flex flex-col overflow-hidden" : "space-y-4"}
      >
        {/* Single card container — matches list view */}
        <div className={`bg-white rounded-xl border border-[#E8EBF1] ${items.length > 0 ? 'flex flex-col min-h-0 flex-1' : ''}`}>
          {/* Header: Title + Summary + Actions */}
          <div className="flex flex-wrap items-center px-5 py-3 border-b border-[#E8EBF1] gap-4">
            {/* Left: Back + Title + Badge */}
            <button onClick={() => setMode('list')} className="p-1.5 rounded-xl hover:bg-[#F8F9FC] text-[rgba(14,19,27,0.6)] transition-colors" title="Esc">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-[#1B202A]">{mode === 'edit' ? 'Edit Challan' : 'New Challan'}</h2>
                <span className="inline-flex items-center px-2 py-0.5 bg-[#0055a9] rounded-lg text-xs font-bold text-white">#{mode === 'edit' ? editingChallanNo : (nextChallanNo || 1)}</span>
              </div>
              {items.length > 0 && (
                <p className="text-xs text-[rgba(14,19,27,0.45)] mt-0.5">{items.length} taka{items.length > 1 ? 's' : ''} added</p>
              )}
            </div>

            <div className="flex-1" />

            {/* Center: Live summary — only when items exist */}
            {items.length > 0 && (
              <div className="hidden lg:flex items-center gap-px bg-[#F1F3F9] rounded-xl p-0.5">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-l-xl">
                  <svg className="w-3.5 h-3.5 text-[rgba(14,19,27,0.35)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                  <span className="text-xs text-[rgba(14,19,27,0.5)]">Pcs</span>
                  <span className="text-sm font-bold text-[#0E131B] tabular-nums">{totalPiece.toFixed(0)}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white">
                  <span className="text-xs text-[rgba(14,19,27,0.5)]">Mtr</span>
                  <span className="text-sm font-bold text-[#0E131B] tabular-nums">{totalMeter.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-r-xl">
                  <span className="text-xs text-[rgba(14,19,27,0.5)]">Amt</span>
                  <span className="text-sm font-bold text-[#0055a9] tabular-nums">{formatINR(totalAmount)}</span>
                </div>
              </div>
            )}

            {/* Right: Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setMode('list')} title="Esc"
                className="flex items-center gap-1.5 bg-white border border-[#E1E6EF] rounded-xl px-3.5 py-2 text-sm font-semibold text-[#0E131B] shadow-[1px_1px_3px_rgba(0,0,0,0.06)] hover:bg-[#F8F9FC] transition-colors">
                Cancel
                <kbd className="hidden lg:inline text-[10px] text-[rgba(14,19,27,0.3)] bg-[#F1F3F9] px-1 py-0.5 rounded font-mono">Esc</kbd>
              </button>
              <button onClick={() => handleSave(false)} disabled={(createMutation.isPending || updateMutation.isPending) || !items.length}
                className="flex items-center gap-1.5 bg-white border border-[#0055a9] rounded-xl px-3.5 py-2 text-sm font-semibold text-[#0055a9] hover:bg-[#F8F9FC] disabled:opacity-50 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
              </button>
              <button onClick={() => handleSave(true)} disabled={(createMutation.isPending || updateMutation.isPending) || !items.length} title="Ctrl+S"
                className="flex items-center gap-1.5 bg-[#0055a9] text-white rounded-xl px-3.5 py-2 text-sm font-semibold shadow-[1px_1px_3px_rgba(0,0,0,0.06)] hover:bg-[#0d2780] disabled:opacity-50 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                Save & Print
                <kbd className="hidden lg:inline text-[10px] text-white/40 bg-white/15 px-1 py-0.5 rounded font-mono">⌘S</kbd>
              </button>
            </div>
          </div>

          {/* Form Section — original grid layout preserved */}
          <div className="px-5 py-5 border-b border-[#E8EBF1] space-y-3">
            {/* Row 1: Party details — ORIGINAL col-span-1+3+4+2+2 */}
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6 sm:col-span-3 lg:col-span-1">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">TYPE</label>
                <select value={formData.acHead} onChange={e => setFormData({ ...formData, acHead: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]">
                  <option value="job">Job</option>
                  <option value="own">Own</option>
                </select>
              </div>
              <div className="col-span-6 sm:col-span-9 lg:col-span-3">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">PARTY NAME <span className="text-[#E02D3C]">*</span></label>
                <ClientDropdown value={formData.partyName} clients={clients} allowCreate={false}
                  onChange={(name, _clientId, address) => setFormData({ ...formData, partyName: name, address: address || formData.address })} />
              </div>
              <div className="col-span-12 sm:col-span-6 lg:col-span-4">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">ADDRESS</label>
                <input type="text" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="Address" />
              </div>
              <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">DATE <span className="text-[#E02D3C]">*</span></label>
                <input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
              </div>
              <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">RATE <span className="text-[#E02D3C]">*</span></label>
                <input type="number" step="0.001" value={formData.rate} onChange={e => {
                  setFormData({ ...formData, rate: e.target.value });
                  setItems(prev => prev.map(it => {
                    const rate = parseFloat(e.target.value) || 0;
                    return { ...it, rate: e.target.value, amount: evalBillingFormula(billingFormula, {
                      rate,
                      meter: parseFloat(it.meter) || 0,
                      avgPick: parseFloat((it as any).avgPick) || 0,
                      piece: parseFloat(it.piece) || 1,
                    }) };
                  }));
                }}
                  className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="0.000" />
              </div>
            </div>

            {/* Row 2: Add Taka — clean grid layout */}
            <div className="pt-3 mt-3 border-t border-[#F1F3F9]">
             
              <div className="grid grid-cols-12 gap-3 items-end">
                {/* Series selector */}
                <div className="col-span-3 sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">SERIES</label>
                  <select
                    value={challanSeries === undefined ? '__all__' : challanSeries === null ? '__default__' : challanSeries}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__all__') setChallanSeries(undefined);
                      else if (v === '__default__') setChallanSeries(null);
                      else setChallanSeries(v);
                    }}
                    className={`w-full px-2 py-1.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] ${
                      challanSeries === undefined
                        ? 'border-[#E1E6EF] bg-white'
                        : 'border-[#0055a9] bg-[#EEF1FB] font-semibold text-[#0055a9]'
                    }`}
                  >
                    <option value="__all__">All Series</option>
                    {challanSeriesList.map((s: { series: string | null; nextTakaNo: number; count: number }) => (
                      <option key={s.series ?? '__default__'} value={s.series === null ? '__default__' : s.series}>
                        {seriesLabel(s.series)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Single Taka — first */}
                <div className="col-span-6 sm:col-span-4 lg:col-span-2">
                  <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">TAKA NO</label>
                  <div className="flex items-center gap-1.5">
                    <input type="number" value={singleTaka} onChange={e => setSingleTaka(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); loadSingleTaka(); } }}
                      className="flex-1 px-2 py-1.5 text-sm font-semibold border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="Enter #" />
                    <button onClick={loadSingleTaka}
                      className="px-3 py-1.5 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] transition-colors whitespace-nowrap">
                      Add
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div className="hidden lg:flex col-span-1 items-center justify-center pb-1">
                  <span className="text-xs text-[rgba(14,19,27,0.3)] font-medium">or</span>
                </div>

                {/* Quality */}
                <div className="col-span-6 sm:col-span-4 lg:col-span-3">
                  <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">QUALITY</label>
                  <ChallanQualityDropdown
                    value={selectedQuality}
                    onChange={v => setSelectedQuality(v)}
                    options={pendingSummary?.summary || []}
                  />
                </div>

                {/* Range */}
                <div className="col-span-3 sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">FROM</label>
                  <input type="number" value={fromTaka} onChange={e => setFromTaka(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); document.querySelector<HTMLInputElement>('[data-field="toTaka"]')?.focus(); } }}
                    className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
                </div>
                <div className="col-span-3 sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">TO</label>
                  <input type="number" value={toTaka} onChange={e => setToTaka(e.target.value)} data-field="toTaka"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); loadTakas(); } }}
                    className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
                </div>

                {/* Actions */}
                <div className="col-span-6 sm:col-span-4 lg:col-span-3 flex items-center gap-2">
                  <button onClick={loadTakas}
                    className="px-4 py-1.5 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] transition-colors">
                    Load Range
                  </button>
                  <button onClick={loadAllTakas} title="Ctrl+L"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-[#0055a9] bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl hover:bg-[#EEF0F6] transition-colors">
                    Load All
                    <kbd className="hidden lg:inline text-[10px] text-[rgba(14,19,27,0.3)] bg-white px-1 py-0.5 rounded font-mono">⌘L</kbd>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Pending Taka Info */}
          {selectedQuality && items.length === 0 && (() => {
            const info = pendingSummary?.summary.find((s: any) => s.quality === selectedQuality);
            if (!info) return null;
            return (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                className="mx-5 mt-4 flex items-center gap-3 bg-[#FCEDB9] border border-[#FCD579] rounded-xl px-4 py-3">
                <svg className="w-5 h-5 text-[#A82C00] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="text-sm text-[#A82C00]">
                  <b>{info.count}</b> pending takas for <b>{selectedQuality}</b>: <span className="font-mono">#{info.ranges}</span>
                </span>
              </motion.div>
            );
          })()}

          {/* Items Table */}
          {items.length > 0 && (
            <div className="overflow-auto flex-1 min-h-0">
              <table className="min-w-full">
                <thead className="sticky top-0 z-10">
                  <tr className="border-y border-[#F1F3F9]">
                    <th className="px-3 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-10">#</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-16">Taka</th>
                    <th className="px-2 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[16%]">Quality</th>
                    <th className="px-2 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[16%]">Desi No</th>
                    <th className="px-2 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[16%]">Colour</th>
                    <th className="px-2 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[10%]">Pick</th>
                    <th className="px-2 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[10%]">Piece</th>
                    <th className="px-2 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[10%]">Meter</th>
                    <th className="px-2 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[10%]">Amount</th>
                    <th className="px-2 py-3 bg-[#F8F9FC] w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <motion.tr key={i}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className="border-b border-[#F1F3F9] hover:bg-[#FAFBFD] transition-colors"
                    >
                      <td className="px-3 py-2 text-center text-xs text-[rgba(14,19,27,0.5)]">{i + 1}</td>
                      <td className="px-3 py-2 text-center text-sm font-semibold text-[#0055a9]">{formatTakaNo(item.series, item.takaNo)}</td>
                      <td className="px-2 py-1.5">
                        <input type="text" value={item.qualityName} onChange={e => updateItem(i, 'qualityName', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-transparent rounded-lg bg-transparent hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white transition-all" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="text" value={item.desiNo} onChange={e => updateItem(i, 'desiNo', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-transparent rounded-lg bg-transparent hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white transition-all" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="text" value={item.colour} onChange={e => updateItem(i, 'colour', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-transparent rounded-lg bg-transparent hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white transition-all" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" value={(item as any).avgPick || ''} onChange={e => updateItem(i, 'avgPick' as any, e.target.value)}
                          className="w-full px-2 py-1.5 text-sm text-right border border-transparent rounded-lg bg-transparent hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white transition-all tabular-nums" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" value={item.piece} onChange={e => updateItem(i, 'piece', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm text-right border border-transparent rounded-lg bg-transparent hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white transition-all tabular-nums" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" value={item.meter} onChange={e => updateItem(i, 'meter', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm text-right font-semibold border border-transparent rounded-lg bg-transparent hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white transition-all tabular-nums" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" value={Math.round(item.amount * 100) / 100} onChange={e => {
                          setItems(prev => { const next = [...prev]; next[i] = { ...next[i], amount: parseFloat(e.target.value) || 0 }; return next; });
                        }}
                          className="w-full px-2 py-1.5 text-sm text-right font-medium border border-transparent rounded-lg bg-transparent hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white transition-all tabular-nums" />
                      </td>
                      <td className="px-2 py-3 text-center">
                        <button onClick={() => removeItem(i)} className="p-1 rounded-lg text-[rgba(14,19,27,0.4)] hover:text-[#E02D3C] hover:bg-red-50 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 z-10">
                  <tr className="border-t-2 border-[#E8EBF1] bg-[#F8F9FC]">
                    <td className="px-4 py-3 text-xs font-bold text-[rgba(14,19,27,0.65)] uppercase" colSpan={6}>Total</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#0E131B] text-right tabular-nums">{totalPiece.toFixed(0)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#0E131B] text-right tabular-nums">{totalMeter.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#0E131B] text-right tabular-nums">{formatINR(totalAmount)}</td>
                    <td className="bg-[#F8F9FC]"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Empty state when no items loaded */}
          {items.length === 0 && !selectedQuality && (
            <div className="px-5 py-12 text-center">
              <svg className="w-12 h-12 mx-auto text-[rgba(14,19,27,0.15)] mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              <p className="text-sm text-[rgba(14,19,27,0.4)] font-medium">Select a quality and load takas to get started</p>
            </div>
          )}

          {/* Order No & Remark — ORIGINAL inline layout */}
          <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-[#F1F3F9]">
            <label className="text-xs font-semibold text-[rgba(14,19,27,0.65)]">ORD NO:</label>
            <input type="text" value={formData.orderNo} onChange={e => setFormData({ ...formData, orderNo: e.target.value })}
              className="w-full sm:w-32 px-2 py-1 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="Order no" />
            <label className="text-xs font-semibold text-[rgba(14,19,27,0.65)]">HSN:</label>
            <input type="text" value={formData.hsnCode} onChange={e => setFormData({ ...formData, hsnCode: e.target.value })}
              className="w-full sm:w-32 px-2 py-1 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="HSN Code" />
            <label className="text-xs font-semibold text-[rgba(14,19,27,0.65)]">REMARK:</label>
            <input type="text" value={formData.remark} onChange={e => setFormData({ ...formData, remark: e.target.value })}
              className="flex-1 min-w-0 w-full sm:w-auto px-2 py-1 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="Remark..." />
          </div>
        </div>

        {/* Mix Quality Confirmation Modal */}
        {mixQualityConfirm && (
          <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            tabIndex={-1}
            ref={el => el?.focus()}
            onKeyDown={e => { if (e.key === 'Escape') setMixQualityConfirm(null); if (e.key === 'Enter') { e.preventDefault(); confirmMixQuality(); } }}>
            <div className="bg-white rounded-xl border border-[#E8EBF1] shadow-[0_7px_29px_rgba(100,100,111,0.2)] w-full max-w-xs sm:max-w-sm p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FCEDB9] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#A82C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1B202A]">Different Quality</h3>
                  <p className="text-sm text-[rgba(14,19,27,0.6)]">Mix qualities in this challan?</p>
                </div>
              </div>
              <p className="text-sm text-[#0E131B] mb-5">
                You're adding takas of quality <b>"{mixQualityConfirm.quality}"</b> but this challan already has takas of a different quality. Do you want to mix them?
              </p>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setMixQualityConfirm(null)}
                  className="px-4 py-2 text-sm font-semibold text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors">
                  Cancel
                </button>
                <button onClick={confirmMixQuality}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] transition-colors">
                  Yes, Mix Qualities
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // LIST VIEW
  const listTotalMeter = challanList.reduce((s: number, c: any) => s + (parseFloat(c.totalMeter) || 0), 0);
  const listTotalAmount = challanList.reduce((s: number, c: any) => s + (parseFloat(c.totalAmount) || 0), 0);
  const listTotalPiece = challanList.reduce((s: number, c: any) => s + (parseFloat(c.totalPiece) || 0), 0);

  return (
    <motion.div
      key="list-mode"
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="space-y-0"
    >
      {/* Stats Card */}
      <div className="bg-white rounded-xl border border-[#E8EBF1] mb-3 p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-0 divide-x-0 lg:divide-x divide-[#E8EBF1]">
          {/* Today's Challans */}
          <div className="flex items-center gap-3 pr-0 lg:pr-4">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <svg className="w-[18px] h-[18px] text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[rgba(14,19,27,0.45)] uppercase tracking-wider leading-none">Today</p>
              <p className="text-lg font-bold text-[#0E131B] leading-tight tabular-nums mt-0.5">₹ {(challanStats?.today?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-[11px] text-[rgba(14,19,27,0.4)] mt-0.5">{challanStats?.today?.count || 0} chln · {(challanStats?.today?.meter || 0).toFixed(2)} mtr</p>
            </div>
          </div>
          {/* Pending */}
          <div className="flex items-center gap-3 px-0 lg:px-4">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <svg className="w-[18px] h-[18px] text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[rgba(14,19,27,0.45)] uppercase tracking-wider leading-none">Pending</p>
              <p className="text-lg font-bold text-amber-600 leading-tight tabular-nums mt-0.5">₹ {(challanStats?.pending?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-[11px] text-[rgba(14,19,27,0.4)] mt-0.5">{challanStats?.pending?.count || 0} chln · {(challanStats?.pending?.meter || 0).toFixed(2)} mtr</p>
            </div>
          </div>
          {/* Billed */}
          <div className="flex items-center gap-3 px-0 lg:px-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <svg className="w-[18px] h-[18px] text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[rgba(14,19,27,0.45)] uppercase tracking-wider leading-none">Billed</p>
              <p className="text-lg font-bold text-emerald-600 leading-tight tabular-nums mt-0.5">₹ {(challanStats?.complete?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-[11px] text-[rgba(14,19,27,0.4)] mt-0.5">{challanStats?.complete?.count || 0} chln · {(challanStats?.complete?.meter || 0).toFixed(2)} mtr</p>
            </div>
          </div>
          {/* Month Total */}
          <div className="flex items-center gap-3 pl-0 lg:pl-4">
            <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
              <svg className="w-[18px] h-[18px] text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[rgba(14,19,27,0.45)] uppercase tracking-wider leading-none">{challanStats?.month?.year && challanStats?.month?.month ? new Date(challanStats.month.year, challanStats.month.month - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) + ' Total' : 'Month Total'}</p>
              <p className="text-lg font-bold text-[#0E131B] leading-tight tabular-nums mt-0.5">₹ {(challanStats?.month?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-[11px] text-[rgba(14,19,27,0.4)] mt-0.5">{challanStats?.month?.count || 0} chln · {(challanStats?.month?.meter || 0).toFixed(2)} mtr</p>
            </div>
          </div>
        </div>
      </div>

      {/* Single white card container matching Figma */}
      <div className="bg-white rounded-xl border border-[#E8EBF1]">
        {/* Header bar: Title + Search + Filters */}
        <div className="flex flex-wrap items-center px-5 py-4 border-b border-[#E8EBF1] gap-3">
          {/* Left: Title */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-[#1B202A]">Challans</h2>
            <span className="text-sm text-[rgba(14,19,27,0.5)] font-medium ml-1">{totalRecords} records</span>
          </div>

          {/* Right: Filters + Actions */}
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            {/* Search */}
            <div className="flex items-center gap-3 bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl px-3 py-2 w-full sm:w-[280px]">
              <svg className="w-5 h-5 text-[#0E131B] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search"
                className="bg-transparent text-sm text-[#0E131B] placeholder:text-[rgba(14,19,27,0.4)] focus:outline-none w-full" />
            </div>

            {/* Date filter */}
            <button className="flex items-center gap-2 bg-white border border-[#E1E6EF] rounded-xl px-4 py-2 text-sm font-semibold text-[#0E131B] shadow-[1px_1px_3px_rgba(0,0,0,0.06)] hover:bg-gray-50 transition-colors">
              <input type="month" value={filterMonth} onChange={e => { setFilterMonth(e.target.value); setPage(1); }}
                className="bg-transparent text-sm font-semibold text-[#0E131B] focus:outline-none cursor-pointer" />
            </button>

            {/* Export month-range PDF */}
            <button onClick={() => { setExportFrom(filterMonth); setExportTo(filterMonth); setShowExportRange(true); }}
              className="flex items-center gap-1.5 bg-white border border-red-600 rounded-xl px-3 h-[38px] text-sm font-semibold text-red-700 shadow-[1px_1px_3px_rgba(0,0,0,0.06)] hover:bg-red-50 transition-colors"
              title="Export challans for a month range as PDF or Excel">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              Export
            </button>

            {/* Print Settings */}
            <div className="relative" ref={printSettingsRef}>
              <button onClick={() => setShowPrintSettings(!showPrintSettings)}
                className="flex items-center justify-center w-[38px] h-[38px] bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] hover:bg-[#F8F9FC] transition-colors"
                title="Print Settings">
                <svg className="w-5 h-5 text-[rgba(14,19,27,0.7)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </button>
              {showPrintSettings && (
                <div className="absolute right-0 top-full mt-2 w-[300px] max-h-[70vh] overflow-y-auto bg-white border border-[#E1E6EF] rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] p-4 z-50">
                  <p className="text-sm font-bold text-[#1B202A] mb-3">Print Layout Settings</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">Rows per column</label>
                      <input type="number" value={printRows} onChange={e => updatePrintRows(parseInt(e.target.value) || 14)} min={1} max={50}
                        className="w-full px-3 py-2 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">Max columns</label>
                      <input type="number" value={printCols} onChange={e => updatePrintCols(parseInt(e.target.value) || 4)} min={1} max={6}
                        className="w-full px-3 py-2 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
                    </div>
                    <p className="text-xs text-[rgba(14,19,27,0.4)]">Settings are saved and apply to all challan prints.</p>
                  </div>
                  {isAdmin && (
                    <div className="mt-3 pt-3 border-t border-[#E1E6EF]">
                      <p className="text-sm font-bold text-[#1B202A] mb-1">Billing Formula</p>
                      <p className="text-xs text-[rgba(14,19,27,0.45)] mb-2">How each taka's amount is calculated. Applies to all challans &amp; bills.</p>
                      <BillingFormulaEditor />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* New Challan */}
            <button onClick={openAdd} disabled={!canWrite}
              title={!canWrite ? 'You have read-only access' : undefined}
              className="flex items-center gap-2 bg-[#0055a9] text-white rounded-xl px-4 py-2 text-sm font-semibold shadow-[1px_1px_3px_rgba(0,0,0,0.06)] hover:bg-[#0d2780] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              New Challan
            </button>
          </div>
        </div>

        {/* Table area */}
        {isLoading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0055a9] border-t-transparent mx-auto mb-3"></div>
            <p className="text-sm text-[rgba(14,19,27,0.5)]">Loading...</p>
          </div>
        ) : challanList.length > 0 ? (
          <>
            {/* Mobile: stacked cards (the desktop table below is hidden on phones) */}
            <div className="sm:hidden divide-y divide-[#F1F3F9]">
              {challanList.map((c: any) => {
                const isBillPending = !(c as any).billId;
                const qualities = Array.from(new Set((c.items || []).map((it: any) => it.qualityName || it.quality_name).filter(Boolean))) as string[];
                return (
                  <div key={c.id} className="px-4 py-3 active:bg-[#FAFBFD] transition-colors"
                    onClick={() => canWrite ? openEdit(c) : setViewChallan(c)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-sm font-bold text-[#0055a9] shrink-0">#{c.challanNo}</span>
                          <span className="text-sm font-semibold text-[#0E131B] truncate">{c.partyName || '—'}</span>
                        </div>
                        <p className="text-xs text-[rgba(14,19,27,0.5)] truncate mt-0.5">
                          {qualities.length ? `${qualities.join(', ')} · ` : ''}{new Date(c.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {c.acHead === 'job' ? 'Job' : 'Own'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold border ${isBillPending ? 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]' : 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]'}`}>
                          {isBillPending ? 'Pending' : 'Billed'}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); if (actionMenuId === c.id) { setActionMenuId(null); } else { const rect = (e.currentTarget as HTMLElement).getBoundingClientRect(); setActionMenuPos({ top: rect.bottom + 4, left: rect.right - 222 }); setActionMenuId(c.id); } }}
                          className="p-2 -mr-1 rounded-lg text-[rgba(14,19,27,0.5)] active:bg-[#F0F2F8]" aria-label="Actions">
                          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><circle cx="10" cy="4" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="10" cy="16" r="1.5" /></svg>
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-[#F8F9FC] px-2 py-1.5 text-center">
                        <p className="text-[10px] text-[rgba(14,19,27,0.4)] uppercase font-semibold leading-none">Piece</p>
                        <p className="text-sm font-bold text-[#0E131B] tabular-nums mt-1">{c.totalPiece || 0}</p>
                      </div>
                      <div className="rounded-lg bg-[#F8F9FC] px-2 py-1.5 text-center">
                        <p className="text-[10px] text-[rgba(14,19,27,0.4)] uppercase font-semibold leading-none">Meter</p>
                        <p className="text-sm font-bold text-[#0E131B] tabular-nums mt-1">{parseFloat(c.totalMeter || 0).toFixed(2)}</p>
                      </div>
                      <div className="rounded-lg bg-[#F8F9FC] px-2 py-1.5 text-center min-w-0">
                        <p className="text-[10px] text-[rgba(14,19,27,0.4)] uppercase font-semibold leading-none">Amount</p>
                        <p className="text-sm font-bold text-emerald-700 tabular-nums mt-1 truncate">{formatINR(parseFloat(c.totalAmount || 0))}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center justify-between bg-[#F8F9FC] px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-wide text-[rgba(14,19,27,0.65)]">Total</span>
                <div className="flex items-center gap-3 text-sm">
                  <span><span className="text-[rgba(14,19,27,0.5)]">Pc </span><span className="font-bold tabular-nums text-[#0E131B]">{listTotalPiece}</span></span>
                  <span><span className="text-[rgba(14,19,27,0.5)]">Mtr </span><span className="font-bold tabular-nums text-[#0E131B]">{listTotalMeter.toFixed(2)}</span></span>
                  <span className="font-bold text-emerald-700 tabular-nums">{formatINR(listTotalAmount)}</span>
                </div>
              </div>
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-[1100px] w-full">
                <thead className="z-10 shadow-[0_1px_0_0_#E8EBF1]">
                  <tr className="border-y border-[#F1F3F9]">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[100px]">#CHALLAN</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[160px]">Party Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[120px]">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[80px]">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[80px]">Items</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[100px]">Piece</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[120px]">Meter</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[120px]">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[120px]">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] w-[70px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {challanList.map((c: any, rowIndex: number) => {
                    const isBillPending = !(c as any).billId;
                    return (
                    <motion.tr
                      key={c.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: rowIndex * 0.03, ease: 'easeOut' }}
                      className="border-b border-[#F1F3F9] hover:bg-[#FAFBFD] transition-colors cursor-pointer"
                      onClick={() => canWrite ? openEdit(c) : setViewChallan(c)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">#{c.challanNo}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-[#0E131B]">{c.partyName || '—'}</div>
                        {c.items?.length > 0 && (() => {
                          const qualities = Array.from(new Set(c.items.map((it: any) => it.qualityName || it.quality_name).filter(Boolean))) as string[];
                          return qualities.length > 0 ? <div className="text-xs text-[rgba(14,19,27,0.5)] truncate max-w-[250px]">{qualities.join(', ')}</div> : null;
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">{new Date(c.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-1.5 py-0 rounded text-xs font-semibold border ${
                          c.acHead === 'job'
                            ? 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]'
                            : 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]'
                        }`}>
                          {c.acHead === 'job' ? 'Job' : 'Own'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{c.items?.length || 0}</td>
                      <td className="px-4 py-4 text-right text-sm text-[#0E131B] tabular-nums">{c.totalPiece || 0}</td>
                      <td className="px-4 py-4 text-right text-sm text-[#0E131B] tabular-nums">{parseFloat(c.totalMeter || 0).toFixed(2)}</td>
                      <td className="px-4 py-4 text-right text-sm font-medium text-[#0E131B] tabular-nums">{formatINR(parseFloat(c.totalAmount || 0))}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <motion.span
                          animate={isBillPending ? { scale: [1, 1.06, 1] } : {}}
                          transition={isBillPending ? { duration: 2, repeat: Infinity, ease: 'easeInOut' } : {}}
                          className={`inline-flex items-center px-1.5 py-0 rounded text-xs font-semibold border ${
                            isBillPending
                              ? 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]'
                              : 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]'
                          }`}
                        >
                          {isBillPending ? 'Pending' : 'Billed'}
                        </motion.span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (actionMenuId === c.id) {
                              setActionMenuId(null);
                            } else {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setActionMenuPos({ top: rect.bottom + 4, left: rect.right - 222 });
                              setActionMenuId(c.id);
                            }
                          }}
                          className="p-1.5 rounded-lg text-[rgba(14,19,27,0.8)] hover:bg-[#F8F9FC] transition-colors"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <circle cx="10" cy="4" r="1.5" />
                            <circle cx="10" cy="10" r="1.5" />
                            <circle cx="10" cy="16" r="1.5" />
                          </svg>
                        </button>
                      </td>
                    </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals summary row — aligned with table columns (desktop only) */}
            <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-[1100px] w-full">
              <tfoot>
                <tr className="border-t border-[#E8EBF1] bg-[#F8F9FC]">
                  <td className="px-4 py-3 w-[100px]" />
                  <td className="px-4 py-3 w-[160px]" />
                  <td className="px-4 py-3 w-[120px]" />
                  <td className="px-4 py-3 w-[80px]" />
                  <td className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.55)] uppercase w-[80px]">Total</td>
                  <td className="px-4 py-3 text-right w-[100px]"><span className="text-sm font-bold text-[#0E131B] tabular-nums">{listTotalPiece}</span></td>
                  <td className="px-4 py-3 text-right w-[120px]"><span className="text-sm font-bold text-[#0E131B] tabular-nums">{listTotalMeter.toFixed(2)}</span></td>
                  <td className="px-4 py-3 text-right w-[120px]"><span className="text-sm font-bold text-[#0055a9] tabular-nums">{formatINR(listTotalAmount)}</span></td>
                  <td className="px-4 py-3 w-[120px]" />
                  <td className="px-4 py-3 w-[70px]" />
                </tr>
              </tfoot>
            </table>
            </div>

            {/* Pagination — Figma style */}
            <div className="flex flex-wrap items-center justify-between px-4 py-4 gap-3">
              {/* Centered page buttons */}
              <div className="flex-1" />
              <div className="flex items-center rounded-xl overflow-hidden border border-[#E1E6EF]">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="flex items-center justify-center w-[34px] h-[38px] bg-white border-r border-[#E1E6EF] text-[rgba(14,19,27,0.8)] hover:bg-gray-50 disabled:opacity-30 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) { pageNum = i + 1; }
                  else if (page <= 3) { pageNum = i + 1; }
                  else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                  else { pageNum = page - 2 + i; }
                  return (
                    <button key={pageNum} onClick={() => setPage(pageNum)}
                      className={`flex items-center justify-center w-[34px] h-[38px] text-sm font-semibold border-r border-[#E1E6EF] transition-colors ${
                        page === pageNum
                          ? 'bg-[#0055a9] text-white'
                          : 'bg-white text-[rgba(14,19,27,0.8)] hover:bg-gray-50'
                      }`}>
                      {pageNum}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="flex items-center justify-center w-[34px] h-[38px] bg-white text-[rgba(14,19,27,0.8)] hover:bg-gray-50 disabled:opacity-30 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>

              {/* Show per page */}
              <div className="flex-1 flex items-center justify-end gap-2">
                <span className="text-sm text-[#0E131B]">Show</span>
                <div className="flex items-center border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] bg-white">
                  <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="px-3 py-2 text-sm text-[#0E131B] bg-transparent focus:outline-none cursor-pointer appearance-none pr-7"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='rgba(14,19,27,0.8)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}>
                    {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="p-16 text-center">
            <p className="text-[rgba(14,19,27,0.5)] font-medium mb-2">No challans for {monthLabel}</p>
            <button onClick={openAdd} disabled={!canWrite} title={!canWrite ? 'You have read-only access' : undefined} className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">New Challan</button>
          </div>
        )}
      </div>

      {/* Action dropdown portal */}
      {createPortal(
        <AnimatePresence>
          {actionMenuId !== null && (
            <motion.div
              ref={actionMenuRef}
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="fixed w-[222px] bg-white rounded-xl border border-[#E1E6EF] shadow-[0_7px_29px_rgba(100,100,111,0.2)] py-1 z-[9999]"
              style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            >
          {(() => {
            const challan = challanList.find((ch: any) => ch.id === actionMenuId);
            if (!challan) return null;
            return (
              <>
                <button
                  onClick={() => { openEdit(challan); setActionMenuId(null); }}
                  disabled={!canWrite}
                  title={!canWrite ? 'You have read-only access' : undefined}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors gap-3 border-b border-[#E1E6EF]"
                >
                  <svg className="w-5 h-5 text-[#0E131B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  <span className="flex-1 text-left">Edit</span>
                </button>
                <button
                  onClick={() => { setViewChallan(challan); setActionMenuId(null); }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] transition-colors gap-3 border-b border-[#E1E6EF]"
                >
                  <svg className="w-5 h-5 text-[#0E131B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  <span className="flex-1 text-left">View</span>
                </button>
                <button
                  onClick={() => { handlePrint(challan); setActionMenuId(null); }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] transition-colors gap-3 border-b border-[#E1E6EF]"
                >
                  <svg className="w-5 h-5 text-[#0E131B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                  <span className="flex-1 text-left">Print</span>
                </button>
                <button
                  onClick={() => { downloadPdf(challan); setActionMenuId(null); }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] transition-colors gap-3"
                >
                  <svg className="w-5 h-5 text-[#0E131B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  <span className="flex-1 text-left">Download PDF</span>
                </button>
              </>
            );
          })()}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* View Challan Modal */}
      <AnimatePresence>
        {viewChallan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4 print:hidden"
            onClick={() => setViewChallan(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="bg-white rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] w-full max-w-sm sm:max-w-2xl lg:max-w-5xl max-h-[90vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Action bar */}
              <div className="flex items-center justify-end gap-2 px-5 py-2.5 border-b border-gray-100">
                <button onClick={() => { handlePrint(viewChallan); setViewChallan(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#0055a9] bg-[#F8F9FC] border border-[#E1E6EF] rounded-lg hover:bg-[#EEF0F6] transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                  Print
                </button>
                <button onClick={() => { downloadPdf(viewChallan); setViewChallan(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#0055a9] bg-[#F8F9FC] border border-[#E1E6EF] rounded-lg hover:bg-[#EEF0F6] transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  PDF
                </button>
                <button onClick={() => setViewChallan(null)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Challan preview - styled like print */}
              <div className="flex-1 overflow-y-auto px-6 py-4" style={{ fontFamily: 'Arial, sans-serif' }}>
                <div className="border-2 border-black">
                  {/* Header — inside outer border */}
                  <div className="px-4 pt-1">
                    <p className="text-center text-[9px] italic text-gray-500">!! ** Shree Ganeshaya Namah ** !!</p>
                    <h1 className="text-center text-2xl font-black uppercase tracking-wide">{(user as any)?.companyName || 'COMPANY NAME'}</h1>
                    {companySettings?.companyAddress && <p className="text-center text-[10px] uppercase text-gray-600">{companySettings.companyAddress}</p>}
                    {companySettings?.companyPhone && <p className="text-center text-[10px] text-gray-600">Mobile : {companySettings.companyPhone}</p>}
                    {companySettings?.companyGst && <p className="text-center text-[10px] text-gray-600">GSTIN : {companySettings.companyGst}</p>}
                  </div>

                  {/* DELIVERY CHALLAN — full-width borders edge-to-edge */}
                  <div className="border-y-2 border-black text-center py-0.5 mt-1">
                    <p className="text-sm font-bold tracking-widest">DELIVERY CHALLAN</p>
                  </div>

                  {/* Party & Challan Info */}
                  <div className="flex justify-between px-4 py-1">
                    <div className="flex-1">
                      <p className="text-sm"><b>Party :</b> {viewChallan.partyName || ''}</p>
                      {viewChallan.address && <p className="ml-8 text-xs text-gray-600">{viewChallan.address}</p>}
                      {(() => {
                        const client = clients.find((c: any) => c.name === viewChallan.partyName);
                        return client?.gstNo ? <p className="text-xs"><b>GSTIN :</b> {client.gstNo}</p> : null;
                      })()}
                    </div>
                    <div>
                      <table className="text-sm border-collapse">
                        <tbody>
                          <tr>
                            <td className="font-bold text-right border border-black px-3 py-0.5">Chln No</td>
                            <td className="border border-black px-4 py-0.5 font-bold text-center min-w-[60px]">{viewChallan.challanNo}</td>
                          </tr>
                          <tr>
                            <td className="font-bold text-right border border-black px-3 py-0.5">Date</td>
                            <td className="border border-black px-4 py-0.5 text-center">{new Date(viewChallan.date).toLocaleDateString('en-IN')}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Quality, Broker Name, HSN Code — full-width borders */}
                  <div className="flex border-y border-black py-0.5 px-4 text-xs" style={{ gap: '0' }}>
                    <span className="flex-1"><b>Quality :</b>{viewChallan.items?.[0]?.qualityName || viewChallan.items?.[0]?.quality_name || ''}</span>
                    <span className="flex-1 text-center"><b>Broker Name :</b></span>
                    <span className="flex-1 text-right"><b>HSN Code :</b>{viewChallan.hsnCode || ''}</span>
                  </div>

                  {/* Items Table — Multi-column grid: Srl, TakaNo, Meters (using printRows/printCols) */}
                  <div className="px-3">
                  {(() => {
                    const allItems = viewChallan.items || [];
                    const rowsPerCol = printRows;
                    const maxCols = printCols;
                    const itemsPerPage = rowsPerCol * maxCols;
                    const pageItems = allItems.slice(0, itemsPerPage);

                    return (
                      <div className="flex" style={{ gap: maxCols > 1 ? '6px' : '0' }}>
                        {Array.from({ length: maxCols }, (_, colIdx) => {
                          const colItems = pageItems.slice(colIdx * rowsPerCol, (colIdx + 1) * rowsPerCol);
                          const globalStartIdx = colIdx * rowsPerCol;
                          const colMeterTotal = colItems.reduce((s: number, item: any) => s + parseFloat(item.meter || 0), 0);
                          return (
                            <table key={colIdx} className="border-collapse text-xs flex-1">
                              <thead>
                                <tr>
                                  <th className="border border-black px-1 py-0.5 text-center bg-gray-100 font-bold" style={{width: '24px'}}>Sr.</th>
                                  <th className="border border-black px-1 py-0.5 text-center bg-gray-100 font-bold">TakaNo</th>
                                  <th className="border border-black px-1 py-0.5 text-center bg-gray-100 font-bold">Meters</th>
                                  <th className="border border-black px-1 py-0.5 text-center bg-gray-100 font-bold" style={{width: '32px'}}>MC No.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {colItems.map((item: any, i: number) => (
                                  <tr key={i}>
                                    <td className="border border-black px-1 py-0.5 text-center text-red-600">{globalStartIdx + i + 1}</td>
                                    <td className="border border-black px-1 py-0.5 text-center font-bold">{formatTakaNo(item.series, item.takaNo || item.taka_no)}</td>
                                    <td className="border border-black px-1 py-0.5 text-right tabular-nums pr-2">{parseFloat(item.meter || 0).toFixed(2)}</td>
                                    <td className="border border-black px-1 py-0.5 text-center">{item.mcNo || item.mc_no || item.takaInventory?.mcNo || item.takaInventory?.mc_no || ''}</td>
                                  </tr>
                                ))}
                                {Array.from({ length: Math.max(0, rowsPerCol - colItems.length) }, (_, i) => (
                                  <tr key={`empty-${i}`}>
                                    <td className="border border-black px-1 py-0.5">&nbsp;</td>
                                    <td className="border border-black px-1 py-0.5">&nbsp;</td>
                                    <td className="border border-black px-1 py-0.5">&nbsp;</td>
                                    <td className="border border-black px-1 py-0.5">&nbsp;</td>
                                  </tr>
                                ))}
                                {/* Column subtotal */}
                                <tr className="font-bold">
                                  <td colSpan={2} className="border border-black px-1 py-0.5 text-right"></td>
                                  <td className="border border-black px-1 py-0.5 text-right tabular-nums pr-2">{colItems.length > 0 ? colMeterTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
                                  <td className="border border-black px-1 py-0.5"></td>
                                </tr>
                              </tbody>
                            </table>
                          );
                        })}
                      </div>
                    );
                  })()}
                  </div>

                  {/* Totals — full-width border */}
                  <div className="flex gap-8 px-4 py-1 border-t-2 border-black font-bold text-sm">
                    <span>Total Taka : {viewChallan.items?.length || 0}</span>
                    <span>Total Mtrs : {parseFloat(viewChallan.totalMeter || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>

                  {/* Remarks */}
                  {viewChallan.remark && (
                    <p className="px-4 text-xs border-t border-black py-0.5">REMARKS:- {viewChallan.remark}</p>
                  )}

                  {/* Footer */}
                  <div className="flex justify-between px-4 py-2 text-xs items-end border-t border-black">
                    <div>
                      <p className="flex items-baseline gap-0">Receiver Sign<span style={{ display: 'inline-block', width: '80px', borderBottom: '1px solid #000', marginLeft: '4px' }}></span></p>
                      <p className="mt-1 flex items-baseline gap-0">Prepared By<span style={{ display: 'inline-block', width: '80px', borderBottom: '1px solid #000', marginLeft: '4px' }}></span><span className="ml-1">{(user as any)?.name || ''}</span></p>
                    </div>
                    <div className="text-right">
                      <p>For, <b>{((user as any)?.companyName || '').toUpperCase()}</b></p>
                      <p className="mt-3">Auth.Signatory</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      {showExportRange && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-4"
          onClick={() => !exportingRange && setShowExportRange(false)}
          onKeyDown={e => { if (e.key === 'Escape' && !exportingRange) setShowExportRange(false); }}>
          <div className="bg-white rounded-xl shadow-lg w-full max-w-xs sm:max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1B202A] mb-1">Export Challans</h3>
            <p className="text-sm text-gray-500 mb-4">Every challan in these months, with month totals and a grand total.</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">From month</label>
                <input type="month" value={exportFrom} max={exportTo || undefined} onChange={e => setExportFrom(e.target.value)} autoFocus
                  className="w-full px-3 py-2 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">To month</label>
                <input type="month" value={exportTo} min={exportFrom || undefined} onChange={e => setExportTo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowExportRange(false)} disabled={!!exportingRange}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors">
                Cancel
              </button>
              <button onClick={exportRangeExcel} disabled={!!exportingRange || !exportFrom || !exportTo || exportFrom > exportTo}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {exportingRange === 'xlsx' ? 'Exporting…' : 'Download Excel'}
              </button>
              <button onClick={exportRangePdf} disabled={!!exportingRange || !exportFrom || !exportTo || exportFrom > exportTo}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {exportingRange === 'pdf' ? 'Exporting…' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingChallanId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-4"
          tabIndex={-1} ref={el => el?.focus()}
          onKeyDown={e => { if (e.key === 'Escape') setDeletingChallanId(null); if (e.key === 'Enter') { e.preventDefault(); deleteMutation.mutate(deletingChallanId); setDeletingChallanId(null); } }}>
          <div className="bg-white rounded-xl shadow-lg w-full max-w-xs sm:max-w-sm p-6">
            <h3 className="text-lg font-bold text-[#1B202A] mb-2">Delete Challan</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to delete this challan? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingChallanId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { deleteMutation.mutate(deletingChallanId); setDeletingChallanId(null); }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Layout - hidden on screen, visible on print — 2 copies per A4 page */}
      {printChallan && (() => {
        const td: React.CSSProperties = { border: '0.6px solid #1a1a1a', textAlign: 'center', verticalAlign: 'middle', fontSize: '10px', padding: '3px 4px', lineHeight: '1.2' };
        const th: React.CSSProperties = { ...td, fontWeight: 700, fontSize: '9px', backgroundColor: '#dde5f5', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#0E131B' };
        const labelCell: React.CSSProperties = { padding: '3px 7px', fontSize: '8.5px', color: '#5b6473', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600 };
        const valueCell: React.CSSProperties = { padding: '3px 7px', fontSize: '10px', fontWeight: 700, color: '#0E131B' };
        const printClient = clients.find((c: any) => c.name === printChallan.partyName);

        const renderChallanCopy = (copyLabel?: string) => {
          const allItems = printChallan.items || [];
          const rowsPerCol = printRows;
          const maxCols = printCols;
          const itemsPerPage = rowsPerCol * maxCols;
          const totalMeter = parseFloat(printChallan.totalMeter || 0);
          const pad = '8px';

          return (
            <div style={{ border: '1.5px solid #0055a9', margin: '2px 8px', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '10px', color: '#0E131B', lineHeight: '1.3' }}>

              {/* Copy label strip */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 10px', fontSize: '8.5px', color: '#0E131B', backgroundColor: '#f4f6fb', borderBottom: '0.6px solid #0055a9' }}>
                <span style={{ letterSpacing: '0.4px', fontStyle: 'italic' }}>!! ** Shree Ganeshaya Namah ** !!</span>
                {copyLabel && <span style={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#0055a9' }}>{copyLabel}</span>}
              </div>

              {/* Company header */}
              <div style={{ padding: '4px 10px 3px', textAlign: 'center', borderBottom: '0.6px solid #1a1a1a' }}>
                <p style={{ margin: 0, fontSize: '15px', fontWeight: 800, letterSpacing: '3px', textTransform: 'uppercase', color: '#0055a9' }}>{(user as any)?.companyName || companySettings?.companyName || 'COMPANY NAME'}</p>
                {companySettings?.companyAddress && <p style={{ margin: 0, fontSize: '8px', color: '#3a4150' }}>{companySettings.companyAddress}</p>}
                <p style={{ margin: 0, fontSize: '8px', color: '#3a4150' }}>
                  {companySettings?.companyPhone && <span><b>Mobile:</b> {companySettings.companyPhone}</span>}
                  {companySettings?.companyPhone && companySettings?.companyGst && <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>}
                  {companySettings?.companyGst && <span><b>GSTIN:</b> {companySettings.companyGst}</span>}
                </p>
              </div>

              {/* DELIVERY CHALLAN band */}
              <div style={{ textAlign: 'center', padding: '2.5px 0', backgroundColor: '#dde5f5', color: '#0055a9', borderTop: '0.6px solid #0055a9', borderBottom: '0.6px solid #0055a9' }}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: 800, letterSpacing: '4px' }}>DELIVERY CHALLAN</p>
              </div>

              {/* Party & Challan Info */}
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <div style={{ flex: 2, padding: `3px ${pad}`, borderRight: '0.6px solid #1a1a1a' }}>
                  <p style={{ margin: 0, fontSize: '7.5px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#0055a9' }}>Delivered To</p>
                  <p style={{ margin: 0, fontSize: '10.5px', fontWeight: 700, color: '#0E131B' }}>{printChallan.partyName || '-'}</p>
                  {printChallan.address && <p style={{ margin: 0, fontSize: '8.5px', color: '#3a4150' }}>{printChallan.address}</p>}
                  {printClient?.gstNo && <p style={{ margin: 0, fontSize: '8.5px' }}><b style={{ color: '#5b6473' }}>GSTIN:</b> <span style={{ fontFamily: 'Menlo, Consolas, monospace', fontWeight: 700 }}>{printClient.gstNo}</span></p>}
                </div>
                <div style={{ flex: 1, fontSize: '9px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', height: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ ...labelCell, padding: '2px 7px', borderBottom: '0.4px solid #c8cfdd' }}>Challan No</td>
                        <td style={{ ...valueCell, padding: '2px 7px', borderBottom: '0.4px solid #c8cfdd', textAlign: 'right', color: '#0055a9' }}>{printChallan.challanNo}</td>
                      </tr>
                      <tr>
                        <td style={{ ...labelCell, padding: '2px 7px' }}>Date</td>
                        <td style={{ ...valueCell, padding: '2px 7px', textAlign: 'right' }}>{new Date(printChallan.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Quality, Broker Name, HSN Code */}
              <div style={{ display: 'flex', borderTop: '0.6px solid #1a1a1a', borderBottom: '0.6px solid #1a1a1a', backgroundColor: '#f4f6fb', padding: `3px ${pad}`, fontSize: '9.5px' }}>
                <span style={{ flex: 1.4 }}><b style={{ color: '#5b6473', textTransform: 'uppercase', fontSize: '8.5px', letterSpacing: '0.3px' }}>Quality:</b> <span style={{ fontWeight: 700 }}>{printChallan.items?.[0]?.qualityName || printChallan.items?.[0]?.quality_name || '-'}</span></span>
                <span style={{ flex: 1, textAlign: 'center' }}><b style={{ color: '#5b6473', textTransform: 'uppercase', fontSize: '8.5px', letterSpacing: '0.3px' }}>Broker:</b> <span style={{ fontWeight: 700 }}>{printChallan.brokerName || '-'}</span></span>
                <span style={{ flex: 0.8, textAlign: 'right' }}><b style={{ color: '#5b6473', textTransform: 'uppercase', fontSize: '8.5px', letterSpacing: '0.3px' }}>HSN:</b> <span style={{ fontWeight: 700, fontFamily: 'Menlo, Consolas, monospace' }}>{printChallan.hsnCode || '-'}</span></span>
              </div>

              {/* Items Table — Multi-column grid: Srl, TakaNo, Meters */}
              <div style={{ padding: `2px ${pad}`, width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
              {(() => {
                const pageItems = allItems.slice(0, itemsPerPage);
                const gap = maxCols >= 5 ? 3 : maxCols >= 3 ? 4 : 6;
                const cellPad = maxCols >= 5 ? '2px 2px' : maxCols >= 3 ? '2.5px 3px' : '3px 4px';
                const cellFont = maxCols >= 5 ? '8.5px' : '9.5px';
                const subLabel = maxCols >= 5 ? '7px' : '8.5px';
                const tdC: React.CSSProperties = { ...td, padding: cellPad, fontSize: cellFont };
                const thC: React.CSSProperties = { ...th, padding: cellPad, fontSize: maxCols >= 5 ? '7.5px' : '9px' };

                return (
                  <div style={{ display: 'flex', gap, width: '100%' }}>
                    {Array.from({ length: maxCols }, (_, colIdx) => {
                      const colItems = pageItems.slice(colIdx * rowsPerCol, (colIdx + 1) * rowsPerCol);
                      const globalStartIdx = colIdx * rowsPerCol;
                      const colMeterTotal = colItems.reduce((s: number, item: any) => s + parseFloat(item.meter || 0), 0);
                      return (
                        <table key={colIdx} style={{ borderCollapse: 'collapse', flex: 1, minWidth: 0, tableLayout: 'fixed', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: maxCols >= 5 ? '14px' : '18px' }} />
                            <col />
                            <col />
                            <col style={{ width: maxCols >= 5 ? '22px' : '28px' }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th style={thC}>Sr</th>
                              <th style={thC}>Taka</th>
                              <th style={thC}>Mtr</th>
                              <th style={thC}>MC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {colItems.map((item: any, i: number) => (
                              <tr key={i} style={{ backgroundColor: i % 2 ? '#fafbfd' : '#fff' }}>
                                <td style={{ ...tdC, color: '#5b6473' }}>{globalStartIdx + i + 1}</td>
                                <td style={{ ...tdC, fontWeight: 700, color: '#0055a9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatTakaNo(item.series, item.takaNo || item.taka_no)}</td>
                                <td style={{ ...tdC, textAlign: 'right', paddingRight: 4, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{parseFloat(item.meter || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td style={{ ...tdC, color: '#3a4150', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.mcNo || item.mc_no || item.takaInventory?.mcNo || item.takaInventory?.mc_no || ''}</td>
                              </tr>
                            ))}
                            {Array.from({ length: Math.max(0, rowsPerCol - colItems.length) }, (_, i) => (
                              <tr key={`empty-${i}`}>
                                <td style={tdC}>&nbsp;</td>
                                <td style={tdC}>&nbsp;</td>
                                <td style={tdC}>&nbsp;</td>
                                <td style={tdC}>&nbsp;</td>
                              </tr>
                            ))}
                            {/* Column subtotal */}
                            <tr style={{ backgroundColor: '#dde5f5', color: '#0E131B' }}>
                              <td colSpan={2} style={{ ...tdC, fontWeight: 700, textAlign: 'right', paddingRight: 4, textTransform: 'uppercase', fontSize: subLabel, letterSpacing: '0.3px' }}>Sub</td>
                              <td style={{ ...tdC, fontWeight: 800, textAlign: 'right', paddingRight: 4, fontVariantNumeric: 'tabular-nums' }}>{colItems.length > 0 ? colMeterTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
                              <td style={tdC}></td>
                            </tr>
                          </tbody>
                        </table>
                      );
                    })}
                  </div>
                );
              })()}
              </div>

              {/* Totals strip */}
              <div style={{ display: 'flex', alignItems: 'center', borderTop: '0.6px solid #1a1a1a' }}>
                <div style={{ flex: 1, padding: `3px ${pad}`, borderRight: '0.6px solid #1a1a1a', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: '8px', fontWeight: 700, color: '#5b6473', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total Taka</span>
                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#0E131B', fontVariantNumeric: 'tabular-nums' }}>{(allItems.length || 0).toLocaleString('en-IN')}</span>
                </div>
                <div style={{ flex: 1.4, padding: `3px ${pad}`, backgroundColor: '#dde5f5', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: '8px', fontWeight: 700, color: '#0055a9', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total Meters</span>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: '#0055a9', fontVariantNumeric: 'tabular-nums' }}>{totalMeter.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span style={{ fontSize: '8.5px', fontWeight: 600, color: '#5b6473' }}>mtr</span>
                </div>
              </div>

              {/* Remark */}
              {printChallan.remark && (
                <div style={{ padding: `2px ${pad}`, fontSize: '9px', borderTop: '0.6px solid #1a1a1a', backgroundColor: '#f4f6fb' }}>
                  <p style={{ margin: 0 }}><b style={{ color: '#5b6473', textTransform: 'uppercase', fontSize: '8px', letterSpacing: '0.3px' }}>Remarks:</b> <span style={{ fontStyle: 'italic' }}>{printChallan.remark}</span></p>
                </div>
              )}

              {/* Footer */}
              <div style={{ display: 'flex', padding: `4px ${pad}`, fontSize: '9px', alignItems: 'flex-end', borderTop: '0.6px solid #1a1a1a', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '8px', color: '#5b6473', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Prepared By: </span>
                  <span style={{ fontSize: '9.5px', fontWeight: 700 }}>{(user as any)?.name || '—'}</span>
                </div>
                <div style={{ width: 150, textAlign: 'center', borderLeft: '0.4px dashed #5b6473', paddingLeft: 8 }}>
                  <p style={{ margin: 0, borderTop: '0.4px solid #1a1a1a', paddingTop: 1, fontSize: '8px', color: '#5b6473' }}>Receiver Signature</p>
                </div>
                <div style={{ width: 180, textAlign: 'center', paddingLeft: 8 }}>
                  <p style={{ margin: 0, fontSize: '9px' }}>For, <b style={{ color: '#0055a9' }}>{((user as any)?.companyName || companySettings?.companyName || '').toUpperCase()}</b></p>
                  <p style={{ margin: '14px 0 0', fontSize: '8px', fontWeight: 700, borderTop: '0.4px solid #1a1a1a', paddingTop: 1 }}>Authorised Signatory</p>
                </div>
              </div>
            </div>
          );
        };

        return (
        <div ref={printRef} className="hidden print:block print:fixed print:inset-0 print:bg-white print:z-[99999]"
          style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#0E131B', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
          <style dangerouslySetInnerHTML={{ __html: `
            @media print {
              @page { size: A4 portrait; margin: 6mm; }
              html, body { margin: 0 !important; padding: 0 !important; height: 100vh !important; overflow: hidden !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box !important; }
            }
          ` }} />
          {/* Copy 1 — top half of page */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {renderChallanCopy('Office Copy')}
          </div>
          {/* Separator */}
          <div style={{ borderTop: '1px dashed #0055a9', margin: '2px 10px', position: 'relative', flexShrink: 0 }}>
            <span style={{ position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', padding: '0 8px', fontSize: '7px', color: '#5b6473', textTransform: 'uppercase', letterSpacing: '0.5px' }}>✂ Cut along this line</span>
          </div>
          {/* Copy 2 — bottom half of page */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {renderChallanCopy('Party Copy')}
          </div>
          {/* Software credit */}
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <p style={{ margin: 0, fontSize: '7px', letterSpacing: '0.3px' }}>
              <span style={{ color: '#8c8c8c' }}>Software by </span>
              <span style={{ color: '#0055a9', fontWeight: 700 }}>VueNexa Enterprises</span>
            </p>
          </div>
        </div>
        );
      })()}
    </motion.div>
  );
};

export default Challan;
